import React, { useCallback, useEffect, useRef, useState } from "react";
import MapView from "@arcgis/core/views/MapView.js";
import {
  configureArcgisApiKey,
  getArcgisApiKey,
  loadMapForPreview,
  loadResource,
  VIEW_LOAD_MS,
  withTimeout,
} from "./arcgisMapUtils";
import { EXPANSION_SEGMENT_WEBMAP_ID } from "./expansionMapConfig";
import {
  applyWorkOrderHighlightToSegments,
  focusWorkOrderOnMap,
} from "./expansionSegmentMapUtils";
import "./expansionMapPreview.css";

function ExpandMapIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M2 6V2h4M10 2h4v4M14 10v4h-4M6 14H2v-4"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Expansion segment map — highlights matching work order and supports expand popout. */
export default function ExpansionWorkOrderMapPreview({ workOrder }) {
  const containerRef = useRef(null);
  const viewRef = useRef(null);
  const [phase, setPhase] = useState("loading");
  const [statusHint, setStatusHint] = useState("Loading map…");
  const [errorMessage, setErrorMessage] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [highlightEnabled, setHighlightEnabled] = useState(true);
  const [notice, setNotice] = useState(null);
  const apiKey = getArcgisApiKey();
  const webmapId =
    (process.env.REACT_APP_ARCGIS_EXPANSION_WEBMAP_ID || "").trim() || EXPANSION_SEGMENT_WEBMAP_ID;
  const workOrderKey = String(workOrder ?? "").trim();

  const resizeView = useCallback(() => {
    const view = viewRef.current;
    if (!view) return;
    window.requestAnimationFrame(() => {
      try {
        view.resize();
      } catch {
        /* ignore */
      }
    });
  }, []);

  useEffect(() => {
    setHighlightEnabled(true);
  }, [workOrderKey]);

  useEffect(() => {
    const el = containerRef.current;

    if (!apiKey) {
      setPhase("error");
      setErrorMessage(
        "Embedded map is disabled: add REACT_APP_ARCGIS_API_KEY when building the frontend (see Dockerfile.prod)."
      );
      return undefined;
    }
    if (!el) {
      setPhase("error");
      setErrorMessage("Map container was not available. Try refreshing the page.");
      return undefined;
    }

    let cancelled = false;
    const isCancelled = () => cancelled;
    configureArcgisApiKey(apiKey);

    setPhase("loading");
    setStatusHint("Loading map…");
    setErrorMessage(null);
    setNotice(null);

    (async () => {
      let map;
      try {
        map = await loadMapForPreview(apiKey, { requireWebMap: true, webmapId });
      } catch (e) {
        if (!isCancelled()) {
          setPhase("error");
          setErrorMessage(
            e?.message ||
              "Could not load the map. Check REACT_APP_ARCGIS_EXPANSION_WEBMAP_ID and API key privileges."
          );
        }
        return;
      }
      if (isCancelled()) return;

      const view = new MapView({
        container: el,
        map,
      });
      viewRef.current = view;

      try {
        await withTimeout(loadResource(view), VIEW_LOAD_MS, "Map view");
      } catch (e) {
        if (!isCancelled()) {
          setPhase("error");
          setErrorMessage(e?.message || "The map view failed to initialize.");
        }
        try {
          view.destroy();
        } catch {
          /* ignore */
        }
        viewRef.current = null;
        return;
      }
      if (isCancelled()) return;

      if (workOrderKey) {
        setStatusHint("Locating segment…");
        const zoomed = await focusWorkOrderOnMap(view, workOrderKey, { highlightEnabled: true });
        if (isCancelled()) return;
        if (!zoomed) {
          setNotice(
            `No segment features found for WO ${workOrderKey}. Showing the web map's saved extent.`
          );
          if (map.initialViewProperties?.viewpoint) {
            try {
              await view.goTo(map.initialViewProperties.viewpoint);
            } catch {
              /* use map default */
            }
          }
        }
      } else if (map.initialViewProperties?.viewpoint) {
        try {
          await view.goTo(map.initialViewProperties.viewpoint);
        } catch {
          /* use map default */
        }
      }

      if (!isCancelled()) setPhase("ready");
    })();

    return () => {
      cancelled = true;
      if (viewRef.current) {
        try {
          viewRef.current.destroy();
        } catch {
          /* ignore */
        }
        viewRef.current = null;
      }
    };
  }, [apiKey, webmapId, workOrderKey]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view?.map || phase !== "ready" || !workOrderKey) return;
    applyWorkOrderHighlightToSegments(view.map, workOrderKey, {
      enabled: highlightEnabled,
    }).catch(() => {});
  }, [highlightEnabled, workOrderKey, phase]);

  useEffect(() => {
    if (!expanded) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    resizeView();
    const onKey = (event) => {
      if (event.key === "Escape") setExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
      resizeView();
    };
  }, [expanded, resizeView]);

  useEffect(() => {
    if (phase !== "ready" || !workOrderKey) return undefined;
    const view = viewRef.current;
    if (!view) return undefined;

    const timer = window.setTimeout(() => {
      resizeView();
      focusWorkOrderOnMap(view, workOrderKey, {
        highlight: false,
        padding: expanded ? 80 : 64,
        expandFactor: expanded ? 1.32 : 1.28,
      }).catch(() => {});
    }, expanded ? 160 : 100);

    return () => window.clearTimeout(timer);
  }, [expanded, phase, workOrderKey, resizeView]);

  const toggleHighlight = () => setHighlightEnabled((on) => !on);
  const closeExpanded = () => setExpanded(false);

  const mapTitle = workOrderKey ? `Segment map — WO ${workOrderKey}` : "Segment map";

  return (
    <div className={`expansion-map-preview ${expanded ? "expansion-map-preview--expanded" : ""}`}>
      {expanded ? (
        <button
          type="button"
          className="expansion-map-backdrop"
          aria-label="Close expanded map"
          onClick={closeExpanded}
        />
      ) : null}

      <div
        className={`project-map-esri-wrap ${phase === "error" ? "project-map-esri-wrap--failed" : ""}`}
      >
        {phase === "error" && errorMessage ? (
          <div className="project-map-esri-error" role="alert">
            <p>{errorMessage}</p>
          </div>
        ) : null}

        <div className={`project-map-esri-stage ${expanded ? "expansion-map-stage--expanded" : ""}`}>
          <div className="expansion-map-expanded-header">
            <h3 className="expansion-map-expanded-title">{mapTitle}</h3>
            <div className="expansion-map-expanded-actions">
              {workOrderKey ? (
                <button
                  type="button"
                  className={`expansion-map-tool-btn ${highlightEnabled ? "expansion-map-tool-btn--active" : ""}`}
                  onClick={toggleHighlight}
                  aria-pressed={highlightEnabled}
                  title={highlightEnabled ? "Show all segments" : "Highlight this segment"}
                >
                  {highlightEnabled ? "Show all segments" : "Highlight segment"}
                </button>
              ) : null}
              <button type="button" className="expansion-map-tool-btn" onClick={closeExpanded}>
                Close
              </button>
            </div>
          </div>

          <div ref={containerRef} className="project-map-esri-container" />

          {phase === "ready" && notice ? (
            <div className="expansion-map-notice" role="status">
              {notice}
            </div>
          ) : null}

          {phase === "ready" && !expanded ? (
            <div className="expansion-map-toolbar">
              {workOrderKey ? (
                <button
                  type="button"
                  className={`expansion-map-tool-btn ${highlightEnabled ? "expansion-map-tool-btn--active" : ""}`}
                  onClick={toggleHighlight}
                  aria-pressed={highlightEnabled}
                  title={highlightEnabled ? "Show all segments" : "Highlight this segment"}
                >
                  {highlightEnabled ? "All" : "Highlight"}
                </button>
              ) : null}
              <button
                type="button"
                className="expansion-map-tool-btn expansion-map-tool-btn--icon"
                onClick={() => setExpanded(true)}
                aria-label="Expand map"
                title="Expand map"
              >
                <ExpandMapIcon />
              </button>
            </div>
          ) : null}

          {phase === "loading" ? (
            <div className="project-map-esri-loading-overlay" aria-busy="true">
              {statusHint}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
